import json5 from 'json5'
import yaml from 'yaml'
import type { z } from 'zod'

/**
 * Guess the document type by checking for a
 */
export function findDocType(val: string) {
  const withoutWhiteSpace = val.trim()
  if (
    // Any leading comments means the file is JSON5 or JSONC
    withoutWhiteSpace.startsWith('//') ||
    // Leading object bracket and we assume JSON
    withoutWhiteSpace.startsWith('{') ||
    // Leading array bracket and we assume JSON. We do not expect this as we should only be dealing with objects
    withoutWhiteSpace.startsWith('[')
  )
    return 'json'

  return 'yaml'
}

/**
 * Parsed a string into a typed object
 * Will handle valid yaml, json, and json5 documents
 * Will return an error if the document is not valid
 *
 * Optionally provide a zod schema to validate the document
 */
export function parseAnything<T = unknown>(
  val: string,
  schema: z.ZodType<T>,
):
  | { success: true; data: T }
  | {
      success: false
      data: null
      error: string
      issues?: z.core.$ZodIssue[]
    } {
  const docType = findDocType(val)

  try {
    const obj = docType === 'yaml' ? yaml.parse(val) : json5.parse(val)

    const result = schema.safeParse(obj)
    if (result.success) {
      return {
        success: true,
        data: result.data,
      }
    }
    return {
      success: false,
      data: null,
      error: 'Document does not match the schema',
      issues: result.error.issues,
    }
  } catch {
    return {
      success: false,
      error: `Could not parse a valid ${docType} document`,
      data: null,
    }
  }
}
